import React, {useEffect, useState} from 'react';
import ChartsService from "../../../../services/ChartsService";
import MultipleChoiceChartComponent from "./MultipleChoiceChartComponent";
import TextChartComponent from "./TextChartComponent";
import SelectsChartComponent from "./SelectsChartComponent";
import MultipleOpenChartComponent from "./MultipleOpenChartComponent";
import NumberChartComponent from "./NumberChartComponent";
import SingleChoiceChartComponent from "./SingleChoiceChartComponent";
import ChartHeaderComponent from "./ChartHeaderComponent";
import ErrorBoundary from "../../../meta/ErrorBoundary";
import SortComponent from "../commons/SortComponent";
import {parseMatrixData, recommendedClusteredChartHeight} from "../../../../utils/ChartsUtils";

function ChartReportBoxComponent(props){

    // The chart width represented as a percent
    // how much width space is the chart allowed to take in its parent
    const chartWidthRatioPercent = 90; // %

    const getInitialChartWidth = () => {
        let parentElement = props.divParentRef.current;
        let parentElementComputedStyle = window.getComputedStyle(parentElement);
        let parentElementMargin = parseInt(parentElementComputedStyle.marginLeft.slice(0, -2));
        let parentElementPadding = parseInt(parentElementComputedStyle.paddingLeft.slice(0, -2));
        let parentDivContentWidth = parentElement.offsetWidth - (parentElementMargin + parentElementPadding);

        let calculatedChartWidth = parentDivContentWidth * (chartWidthRatioPercent/100);
        return parseInt(calculatedChartWidth.toFixed(0));
    };

    const defaultChartConfigurations = {
        height: 500,
        width: getInitialChartWidth(),
        scale: 100,
        marginLeft: 0
    };

    const isMobile = window.innerWidth < 768;

    const question = props.questionData.question;
    const isMultipleMatrix = question.options.multiple ? question.options.multiple : false;

    const [viewType, setViewType] = useState(getDefaultViewType(question));
    const [areDisplaySettingsActive, setAreDisplaySettingActive] = useState(false);
    const [areAnswersLoading, setAreAnswersLoading] = useState(true);
    const [errorLoadingAnswers, setErrorLoadingAnswers] = useState(false);
    const [questionAnswers, setQuestionAnswers] = useState(null);

    const [activeChartConfigurations, setActiveChartConfigurations] = useState(defaultChartConfigurations);
    const [chartConfigurations, setChartConfigurations] = useState(defaultChartConfigurations);

    const [sortingOptions, setSortingOptions] = useState(null);

    useEffect(() => {
        ChartsService.getQuestionStats(props.questionData.question.question_id, props.questionData.surveyId,
            (success) => {
                setQuestionAnswers(success.data);
                setAreAnswersLoading(false);
                afterFetchAnswersActions(success.data);
            }, (error) => {
                if(error.status === 404) {
                    setQuestionAnswers({});
                    setAreAnswersLoading(false);
                } else {
                    setAreAnswersLoading(false);
                    setErrorLoadingAnswers(true);
                }
            }
        );
    }, []);

    function afterFetchAnswersActions(questionAnswers) {
        useRecommendedClusteredChartHeight(questionAnswers, viewType);
    }

    function useRecommendedClusteredChartHeight(questionAnswers, viewType) {
        if (props.questionData.question.type === 'selects' && viewType === 'Clustered') {
            const answerOptionNo = Object.keys(questionAnswers[Object.keys(questionAnswers)[0]]).length - 1;
            const clusterNo = Object.keys(questionAnswers).length;
            const changeHeightInChartConfig = prevChartConf => {
                return {
                    ...prevChartConf,
                    height: recommendedClusteredChartHeight(answerOptionNo, clusterNo, isMobile)
                };
            };
            setChartConfigurations(changeHeightInChartConfig);
            setActiveChartConfigurations(changeHeightInChartConfig);
        }
    }

    function closeSettingsWhenChangingViewType(){
        if(areDisplaySettingsActive){
            toggleChartsSettings();
        }
    }

    function toggleChartsSettings(){
        setAreDisplaySettingActive(!areDisplaySettingsActive);
    }

    function changeToPieChart(){
        if(viewType !== 'pieChart') {
            setViewType('pieChart');
            closeSettingsWhenChangingViewType();
        }
    }
    function changeToBarChart(viewType) {
        if(viewType !== 'barChart') {
            setViewType('barChart');
            closeSettingsWhenChangingViewType();
        }
    }

    function refreshChartLiveSettings() {
        setActiveChartConfigurations(chartConfigurations);
    }

    function changeToStackedChart(){
        viewType !== 'Stacked' ? setViewType('Stacked'): '';
        const backToStackChartConfig = prevChartConf => {
            return {
                ...prevChartConf,
                height: defaultChartConfigurations.height
            };
        };
        setChartConfigurations(backToStackChartConfig);
        setActiveChartConfigurations(backToStackChartConfig);
        closeSettingsWhenChangingViewType();
    }
    function changeToClusteredChart() {
        viewType !== 'Clustered' && setViewType('Clustered');
        useRecommendedClusteredChartHeight(questionAnswers, 'Clustered');
        closeSettingsWhenChangingViewType();
    }

    function getDefaultViewType(question){
        let defaultViewType = '';

        switch (question.type) {
            case "singleChoice":
                defaultViewType = 'barChart';
                break;
            case "multipleChoice":
                defaultViewType = 'barChart';
                break;
            case "selects":
                defaultViewType = isMultipleMatrix
                    ? 'Clustered'
                    : isMobile ? 'Clustered' : 'Stacked';
                break;
            default:
                defaultViewType = 'barChart';
        }
        return defaultViewType;
    }

    function getChartComponent(questionAnswers){
        let chartComponent = null;

        switch (props.questionData.question.type) {
            case "singleChoice":
                chartComponent = <SingleChoiceChartComponent viewType={viewType}
                                                             configuration={activeChartConfigurations}
                                                             questionData={props.questionData}
                                                             questionAnswers={questionAnswers}
                                                             isMobile={isMobile}
                                                             chartIndex={props.chartIndex}
                                                             updateChartInfo={props.updateChartInfo}
                                                             sortingOptions={sortingOptions}
                                                             onChartContainerClick={() => refreshChartLiveSettings()}/>;
                break;
            case "multipleChoice":
                chartComponent = <MultipleChoiceChartComponent viewType={viewType}
                                                               configuration={activeChartConfigurations}
                                                               questionData={props.questionData}
                                                               questionAnswers={questionAnswers}
                                                               isMobile={isMobile}
                                                               chartIndex={props.chartIndex}
                                                               updateChartInfo={props.updateChartInfo}
                                                               sortingOptions={sortingOptions}
                                                               onChartContainerClick={() => refreshChartLiveSettings()}/>;
                break;
            case "text":
                chartComponent =
                    <TextChartComponent configuration={activeChartConfigurations}
                                        questionData={props.questionData}
                                        questionAnswers={questionAnswers}
                                        isMobile={isMobile}
                                        chartIndex={props.chartIndex}
                                        updateChartInfo={props.updateChartInfo}
                                        onChartContainerClick={() => refreshChartLiveSettings()}/>;
                break;
            case "selects":
                chartComponent = <SelectsChartComponent viewType={viewType}
                                                        configuration={activeChartConfigurations}
                                                        questionData={props.questionData}
                                                        questionAnswers={questionAnswers}
                                                        isMobile={isMobile}
                                                        chartIndex={props.chartIndex}
                                                        updateChartInfo={props.updateChartInfo}
                                                        sortingOptions={sortingOptions}
                                                        onChartContainerClick={() => refreshChartLiveSettings()}/>;
                break;
            case "multipleOpen":
                chartComponent =
                    <MultipleOpenChartComponent configuration={activeChartConfigurations}
                                                questionData={props.questionData}
                                                questionAnswers={questionAnswers}
                                                isMobile={isMobile}
                                                chartIndex={props.chartIndex}
                                                updateChartInfo={props.updateChartInfo}
                                                onChartContainerClick={() => refreshChartLiveSettings()}/>;
                break;
            case "number":
                chartComponent =
                    <NumberChartComponent configuration={activeChartConfigurations}
                                          questionData={props.questionData}
                                          questionAnswers={questionAnswers}
                                          chartIndex={props.chartIndex}
                                          updateChartInfo={props.updateChartInfo}
                                          onChartContainerClick={() => refreshChartLiveSettings()}/>;
                break;
            default:
                break;
        }

        return chartComponent;
    }

    const getStylesForSettingsMenuTransition = () => {
        let styles = {
            mobileModeTitleDiv: {},
            desktopModeContentDiv: {}
        };

        if(areDisplaySettingsActive) {
            if(window.innerWidth < 768) {
                styles.mobileModeTitleDiv.paddingTop = '65px';
            } else {
                styles.desktopModeContentDiv.paddingTop = '75px';
            }
        } else {
            if(window.innerWidth < 768) {
                styles.mobileModeTitleDiv.paddingTop = '15px';
            } else {
                styles.desktopModeContentDiv.paddingTop = '50px';
            }
        }
        return styles
    };

    const sortingHandler = (option) => {
        setSortingOptions(option);
    };


    const getSortComponent = () => {
      let sortComponent = null;
      switch (props.questionData.question.type) {
          case "singleChoice" :
              sortComponent =  <SortComponent  sortingHandler={sortingHandler} questionId={props.questionData.question.question_id} />;
              break;
          case "multipleChoice":
              sortComponent =  <SortComponent  sortingHandler={sortingHandler} questionId={props.questionData.question.question_id} />;
              break;
          case "selects":
              if(questionAnswers !== null) {
                  const columnNames = parseMatrixData(questionAnswers)[0];
                  delete columnNames.choiceLabel;
                  sortComponent =
                      <SortComponent answer={columnNames} sortingHandler={sortingHandler}
                                     questionId={props.questionData.question.question_id}/>;
              }
              break;
          default:
              break;
      }

      return sortComponent;
    };

    const settingsMenuTransitionStyles = getStylesForSettingsMenuTransition();

    return(
        <React.Fragment>
            <div className="questionaire__reports__container">
                <ChartHeaderComponent viewType={viewType}
                                      titleStyle={settingsMenuTransitionStyles.mobileModeTitleDiv}
                                      questionData={props.questionData}
                                      changeToBarChart={changeToBarChart}
                                      changeToPieChart={changeToPieChart}
                                      changeToStackedChart={changeToStackedChart}
                                      changeToClusteredChart={changeToClusteredChart}
                                      toggleChartsSettings={toggleChartsSettings}
                                      areDisplaySettingsActive={areDisplaySettingsActive}
                                      chartConfigurations={chartConfigurations}
                                      setChartConfigurations={setChartConfigurations}
                                      refreshChart={refreshChartLiveSettings}
                                      isMultipleMatrix={isMultipleMatrix}
                                      isMobile={isMobile}
                />
                {
                    getSortComponent()
                }
                <div onClick={refreshChartLiveSettings} className="questionaire__reports__container__content"
                     id={"reports_container_content_" + props.questionData.question.question_id} style={settingsMenuTransitionStyles.desktopModeContentDiv}>

                    {areAnswersLoading ?
                        (<div>Loading...</div>)
                        :
                        (errorLoadingAnswers ?
                                (<div>Error loading answers!</div>)
                                :
                                (<ErrorBoundary boundaryName="Chart Component">
                                        {getChartComponent(questionAnswers)}
                                        {props.questionData.question.type !== "number" && <div className="chart-mask" />}
                                </ErrorBoundary>)
                        )
                    }
                </div>
            </div>
        </React.Fragment>)

}

export default ChartReportBoxComponent;